import { cn, Switch, Text } from '@risksmart-app/atomic-ui';
import { Controller, useFormContext } from 'react-hook-form';

import {
  type FieldEditorValues,
  isOptionFieldType,
  supportsDefaultValue,
} from '../config';
import { FormSelectField, FormTextField } from './FormFields';
import { OptionsEditor } from './OptionsEditor';

export const FieldEditorConfigFields = ({
  disableFieldType,
  disableFieldName,
  fieldTypeOptions,
  fieldTypeDisabledReason,
  originalLabel,
  isAltValuesEnabled,
}: {
  disableFieldType?: boolean;
  disableFieldName?: boolean;
  fieldTypeOptions: { value: string; label: string }[];
  fieldTypeDisabledReason?: string;
  originalLabel?: string;
  isAltValuesEnabled?: boolean;
}) => {
  const { watch, control } = useFormContext<FieldEditorValues>();
  const fieldType = watch('fieldType');
  const showOptions = isOptionFieldType(fieldType);
  const isShowingAltValues = watch('showAltValues');
  const isAltValuesToggleVisible =
    showOptions && !disableFieldType && isAltValuesEnabled;
  const defaultValueEnabled = watch('defaultValueEnabled');
  const fieldName = watch('fieldName');
  const showOriginalLabel =
    !!originalLabel && originalLabel.length > 0 && fieldName !== originalLabel;

  const fieldNameLabel = showOriginalLabel ? (
    <span className={cn('flex flex-col gap-0.5')}>
      <span>
        {'Field name'}
        <span className={cn('text-destructive ml-0.5')}>{'*'}</span>
      </span>
      <span
        className={cn('text-sm font-normal animate-fade-slide-in')}
        data-testid={'original-label-hint'}
      >
        {`Original label: ${originalLabel}`}
      </span>
    </span>
  ) : (
    'Field name'
  );

  return (
    <div className={cn('flex flex-col gap-4')}>
      <div className={cn('flex flex-col gap-1')}>
        <FormSelectField
          name={'fieldType'}
          label={'Field type'}
          placeholder={'Select a field type'}
          options={fieldTypeOptions}
          disabled={disableFieldType}
        />
        {disableFieldType && fieldTypeDisabledReason ? (
          <Text
            preset={'body-sm'}
            className={cn('text-neutral-muted-foreground')}
          >
            {fieldTypeDisabledReason}
          </Text>
        ) : null}
      </div>
      <FormTextField
        name={'fieldName'}
        label={fieldNameLabel}
        placeholder={'Enter field name'}
        disabled={disableFieldName}
        optional={showOriginalLabel ? true : undefined}
      />
      {isAltValuesToggleVisible ? (
        <div className={cn('flex flex-col gap-3')}>
          <Controller
            control={control}
            name={'showAltValues'}
            render={({ field }) => (
              <label className={cn('flex items-center gap-2 cursor-pointer')}>
                <Text preset={'body-sm'} render={<span />}>
                  {'Show values'}
                </Text>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  size={'sm'}
                  data-testid={'showAltValues'}
                />
              </label>
            )}
          />
          <Text
            preset={'body-sm'}
            className={cn('text-neutral-muted-foreground')}
          >
            {
              'When enabled, you can choose to export either the field names or their stored values from the register'
            }
          </Text>
          {isShowingAltValues ? (
            <div className={cn('flex flex-col gap-1')}>
              <FormTextField
                name={'altLabel'}
                label={'Label value'}
                placeholder={'Enter label value'}
              />
              <Text
                preset={'body-sm'}
                className={cn('text-neutral-muted-foreground')}
              >
                {
                  'This is a code which will be used in place of the label in any data exports.'
                }
              </Text>
            </div>
          ) : null}
        </div>
      ) : null}
      {showOptions && !disableFieldType ? <OptionsEditor /> : null}
      {supportsDefaultValue(fieldType) ? (
        <div className={cn('flex flex-col gap-3')}>
          <Controller
            control={control}
            name={'defaultValueEnabled'}
            render={({ field }) => (
              <label className={cn('flex items-center gap-2 cursor-pointer')}>
                <Text preset={'body-sm'} render={<span />}>
                  {'Set default value'}
                </Text>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  size={'sm'}
                />
              </label>
            )}
          />
          {defaultValueEnabled ? (
            <FormTextField
              name={'defaultValue'}
              label={'Default value'}
              placeholder={'Enter a default value'}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
